import type { ReactNode } from 'react';
import { ChevronDown, Folder, Loader2, Monitor, Square } from 'lucide-react';
import type { DesktopAgentController } from './use-desktop-agent';
import type { CanvasChatMessage } from './canvas-types';
import './desktop-agent.css';

export function DesktopAgentControls({ agent, brandSelector }: { agent: DesktopAgentController; brandSelector?: ReactNode }) {
  if (!agent.available || !agent.authenticatedAccount) return null;
  const workspace = agent.state?.workspace;
  const folderName = workspace?.split(/[\\/]/).filter(Boolean).at(-1);
  return <div className="desktop-agent-controls sentry-block" data-testid="desktop-agent-controls">
    <div className="desktop-agent-context">
      <button className="desktop-agent-folder" type="button" onClick={() => void agent.chooseFolder()} disabled={agent.busy || !agent.state?.signedIn || agent.state.running} data-testid="desktop-agent-folder"
        aria-label={workspace ? `Change working folder: ${folderName}` : 'Choose working folder'} title={workspace ?? 'Choose a working folder. Files needed for your task are processed by Admoji and use your account credits.'}>
        <Folder size={16} aria-hidden="true" /><span>{folderName || 'Choose folder'}</span><ChevronDown size={12} aria-hidden="true" />
      </button>
      <span className="desktop-agent-computer"><Monitor size={16} aria-hidden="true" />This computer</span>
      {brandSelector && <div className="desktop-agent-brand" aria-label="Brand"><span className="desktop-agent-brand-label">Brand</span>{brandSelector}</div>}
      {agent.state?.running && <span className="desktop-agent-running" role="status"><Loader2 size={13} className="animate-spin" aria-hidden="true" />{agent.runningHere ? 'Working…' : 'Working in another chat'}</span>}
      {agent.state?.running && <button className="desktop-agent-stop" type="button" onClick={() => void agent.stop()} disabled={agent.busy} data-testid="desktop-agent-stop"><Square size={12} aria-hidden="true" />Stop</button>}
    </div>
    <span className="sr-only" role="status">{agent.state?.status ?? 'Connecting to the desktop agent…'}</span>
    {agent.error && <p role="alert" className="desktop-agent-error">{agent.error}</p>}
    {agent.approvals.map(request => <section key={request.id} className="desktop-agent-approval" aria-label="Permission required" data-testid="desktop-agent-approval">
      <strong>Allow {request.toolName}?</strong>
      <p>{request.reason || 'Review this action before allowing it.'}</p>
      <details><summary>Review action details</summary><pre>{JSON.stringify(request.input, null, 2)}</pre></details>
      <div className="desktop-agent-approval-actions">
        <button type="button" disabled={agent.busy} onClick={() => void agent.decide(request.id, 'rejected')}>Reject</button>
        <button type="button" disabled={agent.busy} onClick={() => void agent.decide(request.id, 'allowed-once')}>Allow once</button>
      </div>
    </section>)}
  </div>;
}

export function DesktopToolMessage({ message }: { message: CanvasChatMessage }) {
  return <details className="desktop-agent-tool sentry-block" data-testid={`desktop-tool-${message.id}`}>
    <summary>{message.desktopTool?.label || (message.desktopTool?.kind === 'error' ? 'Desktop task error' : 'Local tool')}</summary>
    <pre>{message.content}</pre>
  </details>;
}
